import z from "zod";
import { baseEntitySchema } from "./base";

export const pricingMatrixTypeSchema = z.enum(["structure", "accessory"]);

export type PricingMatrixType = z.infer<typeof pricingMatrixTypeSchema>;

export const pricingMatrixSourceSchema = z.object({
  workbookName: z.string(),
  spreadsheetId: z.string(),
  priceSheetName: z.string(),
  costSheetName: z.string(),
  priceFetchRange: z.string(),
  costFetchRange: z.string(),
});

export type PricingMatrixSource = z.infer<typeof pricingMatrixSourceSchema>;

export const pricingMatrixRowSchema = z.object({
  values: z.array(z.number().nullable()),
});

export type PricingMatrixRow = z.infer<typeof pricingMatrixRowSchema>;

export const pricingMatrixSchema = z
  .object({
    variantKey: z.string(),
    variantLabel: z.string(),
    rowAxisLabel: z.string(),
    colAxisLabel: z.string(),
    rowAxisInches: z.array(z.number()),
    colAxisInches: z.array(z.number()),
    costRows: z.array(pricingMatrixRowSchema),
    priceRows: z.array(pricingMatrixRowSchema),
    source: pricingMatrixSourceSchema,
  })
  .superRefine((matrix, context) => {
    if (matrix.costRows.length !== matrix.rowAxisInches.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "costRows count must match rowAxisInches length.",
        path: ["costRows"],
      });
    }

    if (matrix.priceRows.length !== matrix.rowAxisInches.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "priceRows count must match rowAxisInches length.",
        path: ["priceRows"],
      });
    }

    matrix.costRows.forEach((row, i) => {
      if (row.values.length !== matrix.colAxisInches.length) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `costRows[${i}].values length must match colAxisInches length.`,
          path: ["costRows", i],
        });
      }
    });

    matrix.priceRows.forEach((row, i) => {
      if (row.values.length !== matrix.colAxisInches.length) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `priceRows[${i}].values length must match colAxisInches length.`,
          path: ["priceRows", i],
        });
      }
    });
  });

export type PricingMatrix = z.infer<typeof pricingMatrixSchema>;

export const pricingProductConfigurationSchema = z.object({
  key: z.string(),
  label: z.string(),
  matrixType: pricingMatrixTypeSchema,
  variants: z.record(z.string(), pricingMatrixSchema),
});

export type PricingProductConfiguration = z.infer<
  typeof pricingProductConfigurationSchema
>;

export const pricingConfigurationSourceWorkbookSchema = z.object({
  workbookName: z.string(),
  spreadsheetId: z.string(),
  schemaVersion: z.literal(3),
  syncedAt: z.date(),
});

export type PricingConfigurationSourceWorkbook = z.infer<
  typeof pricingConfigurationSourceWorkbookSchema
>;

export const pricingHeightAdjustmentSourceSchema = z.object({
  workbookName: z.string(),
  spreadsheetId: z.string(),
  sheetName: z.string(),
  fetchRange: z.string(),
});

export type PricingHeightAdjustmentSource = z.infer<
  typeof pricingHeightAdjustmentSourceSchema
>;

export const pricingHeightAdjustmentVariantSchema = z
  .object({
    variantKey: z.string(),
    variantLabel: z.string(),
    rowAxisInches: z.array(z.number()),
    costIncrements: z.array(z.number()),
    priceIncrements: z.array(z.number()),
  })
  .superRefine((variant, context) => {
    if (variant.costIncrements.length !== variant.rowAxisInches.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "costIncrements length must match rowAxisInches length.",
        path: ["costIncrements"],
      });
    }
    if (variant.priceIncrements.length !== variant.rowAxisInches.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "priceIncrements length must match rowAxisInches length.",
        path: ["priceIncrements"],
      });
    }
  });

export type PricingHeightAdjustmentVariant = z.infer<
  typeof pricingHeightAdjustmentVariantSchema
>;

export const pricingHeightAdjustmentsSchema = z.object({
  rowAxisLabel: z.string(),
  variants: z.record(z.string(), pricingHeightAdjustmentVariantSchema),
  source: pricingHeightAdjustmentSourceSchema,
});

export type PricingHeightAdjustments = z.infer<
  typeof pricingHeightAdjustmentsSchema
>;

/**
 * Flat-priced add-on product (Heater, etc.) — no width/height/depth matrix,
 * just a single unit cost + unit price. Quantity-multiplied at order time.
 */
export const pricingFlatProductSourceSchema = z.object({
  workbookName: z.string(),
  spreadsheetId: z.string(),
  priceSheetName: z.string(),
  costSheetName: z.string(),
  priceFetchRange: z.string(),
  costFetchRange: z.string(),
});

export type PricingFlatProductSource = z.infer<
  typeof pricingFlatProductSourceSchema
>;

export const pricingFlatProductSchema = z.object({
  key: z.string(),
  label: z.string(),
  unitCost: z.number(),
  unitPrice: z.number(),
  source: pricingFlatProductSourceSchema,
});

export type PricingFlatProduct = z.infer<typeof pricingFlatProductSchema>;

export const pricingConfigurationDataSchema = z
  .object({
    sourceWorkbook: pricingConfigurationSourceWorkbookSchema,
    structure: pricingProductConfigurationSchema,
    accessories: z.record(z.string(), pricingProductConfigurationSchema),
    heightAdjustments: pricingHeightAdjustmentsSchema.optional(),
    flatProducts: z.record(z.string(), pricingFlatProductSchema).optional(),
  })
  .superRefine((configuration, context) => {
    if (configuration.structure.matrixType !== "structure") {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Structure pricing must use the structure matrix type.",
        path: ["structure", "matrixType"],
      });
    }

    Object.entries(configuration.accessories).forEach(([key, accessory]) => {
      if (accessory.matrixType !== "accessory") {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Accessory pricing "${key}" must use the accessory matrix type.`,
          path: ["accessories", key, "matrixType"],
        });
      }
    });
  });

export type PricingConfigurationData = z.infer<
  typeof pricingConfigurationDataSchema
>;

export const pricingConfigurationSchema = baseEntitySchema.and(
  pricingConfigurationDataSchema,
);

export type PricingConfiguration = z.infer<typeof pricingConfigurationSchema>;
